import React, { useEffect, useState } from 'react';

import DisabledFeatureAlert from '~/components/Shared/DisabledFeatureAlert/DisabledFeatureAlert';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import * as toastTypes from '~/types/toast.types';
import evisortLogoSrc from '~/ui/assets/images/logo-evisort.svg';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import CardList from '~/ui/components/CardList/CardList';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import TextInput from '~/ui/components/TextInput/TextInput';
import { copyToClipboard } from '~/utils/helper.utils';
import { featureFlagIncluded } from '~/utils/user';

import ApiKeysTable from './ApiKeysTable';
import IntegrationDetailCard from './IntegrationDetailCard';

function ApiManagement({
  // injected
  currentUser,
}) {
  const isFeatureEnabled = featureFlagIncluded(
    currentUser,
    FeatureFlagType.ExternalApi,
  );

  const intl = useIntl();

  const errorToastMessage = intl.formatMessage({
    id: 'ApiManagement.failedToLoadApiKeys',
    defaultMessage: 'Failed to load API keys. Please try again.',
    description: 'Error message when API keys fail to load',
  });
  const successToastMessage = intl.formatMessage({
    id: 'ApiManagement.apiKeyCreated',
    defaultMessage: 'New API key generated.',
    description: 'Toast message when a new API key is successfully created',
  });

  const [createdApiKey, setCreatedApiKey] = useState(null);

  const result = api.endpoints.getApiKeys.useQuery(undefined, {
    skip: !isFeatureEnabled,
  });
  const { data: apiKeys = [], isError, isLoading } = result;

  useEffect(() => {
    if (isError) {
      showToast(toastTypes.ERROR, errorToastMessage);
    }
  }, [isError]);

  const [executor, createResult] = api.endpoints.createApiKey.useMutation();
  const { data: newKey, isLoading: isGeneratingKey, isSuccess } = createResult;

  useEffect(() => {
    if (isSuccess && newKey) {
      showToast(toastTypes.SUCCESS, successToastMessage);
      setCreatedApiKey(newKey);
    }
  }, [isSuccess, newKey]);

  if (!isFeatureEnabled) {
    return <DisabledFeatureAlert />;
  }

  const existingKey = apiKeys.find(
    (key) => key.user_id === currentUser.id && key.is_enabled,
  );

  return (
    <CardList
      title={intl.formatMessage({
        id: 'ApiManagement.apiManagementTitle',
        defaultMessage: 'API Management',
        description: 'Title for API Management page',
      })}
    >
      <LoadingContainer isLoading={isLoading}>
        <IntegrationDetailCard
          title={intl.formatMessage({
            id: 'ApiManagement.evisortApiKeysTitle',
            defaultMessage: 'Evisort API Keys',
            description: 'Title for Evisort API Keys section',
          })}
          logoSrc={evisortLogoSrc}
        >
          <FlexLayout flexDirection="column" space={12}>
            <FlexLayout flexDirection="column" space={6}>
              {createdApiKey ? (
                <FlexLayout flexDirection="column" space={4}>
                  <Text color="gray-800" variant="s-dense-bold">
                    {intl.formatMessage({
                      id: 'ApiManagement.apiKeyCreatedMessage',
                      defaultMessage:
                        'Copy your API key and treat it like a password. For your security we will not show it again!',
                      description:
                        'Message displayed when an API key is created',
                    })}
                  </Text>
                  <FlexLayout>
                    <Box sx={{ width: '752px' }}>
                      <TextInput
                        disabled
                        placeholder={createdApiKey.raw_key_nonce}
                        width="fullWidth"
                        onChange={() => {}}
                      />
                    </Box>
                    <Button
                      text={intl.formatMessage({
                        id: 'ApiManagement.copyApiKeyButton',
                        defaultMessage: 'Copy',
                        description:
                          'Button text to copy the API key | maxLength=30',
                      })}
                      onClick={() =>
                        copyToClipboard(
                          createdApiKey.raw_key_nonce,
                          intl.formatMessage({
                            id: 'ApiManagement.apiKeyCopied',
                            defaultMessage: 'API key copied to clipboard.',
                            description:
                              'Message when API key is copied to clipboard',
                          }),
                        )
                      }
                    />
                  </FlexLayout>
                </FlexLayout>
              ) : (
                <Text
                  color="gray-800"
                  variant="s-dense"
                  sx={{ width: '835px' }}
                >
                  <FormattedMessage
                    id="ApiManagement.apiKeyHelperText"
                    defaultMessage="Need access to Evisort outside of our platform? Our API allows you to upload/download documents, extract metadata, train models, search and <link>much more</link>. Create an API key below to start building your Evisort integration."
                    description="Helper text for API key creation"
                    values={{
                      link: (chunks) => (
                        <Link href="https://developers.evisort.com">
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                </Text>
              )}
              <FlexLayout
                flexDirection="column"
                alignItems="flex-start"
                space={3}
              >
                {!!existingKey && (
                  <Alert enableIcon variant="warning">
                    <Text color="gray-800" variant="xs-dense-medium">
                      <FormattedMessage
                        id="ApiManagement.apiKeyAlreadyExists"
                        defaultMessage={
                          'You’ve already created an API token ({keyName}). To create an additional key, first disable the currently active one.'
                        }
                        description="Message shown when an API key already exists.Example: keyName = 'Production API Key'"
                        values={{
                          keyName:
                            existingKey.name ||
                            intl.formatMessage({
                              id: 'ApiManagement.defaultApiKeyName',
                              defaultMessage: 'Untitled API Key',
                              description: 'Default name for an API key',
                            }),
                        }}
                      />
                    </Text>
                  </Alert>
                )}
                <Button
                  disabled={!!existingKey}
                  isLoading={isGeneratingKey}
                  text={intl.formatMessage({
                    id: 'ApiManagement.generateNewApiKeyButton',
                    defaultMessage: 'Generate new API key',
                    description:
                      'Button text to generate a new API key | maxLength=60',
                  })}
                  onClick={executor}
                />
              </FlexLayout>
            </FlexLayout>
            {!!apiKeys.length && (
              <ApiKeysTable
                currentUser={currentUser}
                keys={apiKeys}
                newKeyId={createdApiKey?.id}
              />
            )}
          </FlexLayout>
        </IntegrationDetailCard>
      </LoadingContainer>
    </CardList>
  );
}

export default withCurrentUser(ApiManagement);
